import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { FlightScreen } from '../features/flight/FlightScreen';
import { FlightsScreen } from '../features/flights/FlightsScreen';
import { InboxScreen } from '../features/inbox/InboxScreen';
import { MapScreen } from '../features/map/MapScreen';
import { BriefScreen, NewsScreen } from '../features/news/NewsScreen';
import { PlanEditor } from '../features/plan/PlanEditor';
import { PlanSheet } from '../features/plan/PlanSheet';
import { SearchScreen } from '../features/search/SearchScreen';
import { SettingsScreen } from '../features/settings/SettingsScreen';
import { StatsScreen } from '../features/stats/StatsScreen';
import { TripScreen } from '../features/trip/TripScreen';
import { TripsScreen } from '../features/trips/TripsScreen';
import { BalancesScreen } from '../features/wallet/BalancesScreen';
import { CardScreen } from '../features/wallet/CardScreen';
import { ProgramScreen } from '../features/wallet/ProgramScreen';
import { WalletEditor, type WalletKind } from '../features/wallet/WalletEditor';
import { WalletScreen } from '../features/wallet/WalletScreen';
import { isNative } from '../lib/platform';
import { back, match, type Route, useRouter } from '../lib/router';
import { DESKTOP, useMedia } from '../lib/useMedia';
import { PullToRefresh } from '../ui/PullToRefresh';
import { UpdatePrompt } from '../ui/UpdatePrompt';
import s from './Shell.module.css';
import { Sidebar } from './Sidebar';
import { TabBar } from './TabBar';

const FlightMapScreen = lazy(() => import('../features/map/FlightMapScreen'));

const TAB_ROOTS = new Set(['trips', 'flights', 'wallet', 'news', 'stats']);

/** The screen to show under an overlay that was opened by a deep link. */
function defaultBase(route: Route): Route {
  if (route.name === 'plan') return match(`/trips/${route.params.tripId}`);
  const trip = route.query.get('trip');
  if (route.name === 'newPlan' && trip) return match(`/trips/${trip}`);
  return match('/');
}

function ScreenFor({ route, desktop }: { route: Route; desktop: boolean }) {
  switch (route.name) {
    case 'trips':
      return desktop ? <TripsPanes /> : <TripsScreen />;
    case 'search':
      return <SearchScreen />;
    case 'trip':
      return desktop ? (
        <TripsPanes tripId={route.params.tripId!} />
      ) : (
        <TripScreen tripId={route.params.tripId!} />
      );
    case 'flight':
      return <FlightScreen itemId={route.params.itemId!} />;
    case 'flights':
      return <FlightsScreen />;
    case 'wallet':
      return <WalletScreen />;
    case 'walletCard':
      return <CardScreen id={route.params.recordId!} />;
    case 'walletProgram':
      return <ProgramScreen id={route.params.recordId!} />;
    case 'walletHistory':
      return <BalancesScreen entry={route.query.get('entry')} />;
    case 'map':
      return <MapScreen />;
    case 'news':
      return <NewsScreen />;
    case 'brief':
      return <BriefScreen id={route.params.briefId!} />;
    case 'stats':
      return <StatsScreen />;
    case 'flightMap':
      return (
        <Suspense fallback={null}>
          <FlightMapScreen />
        </Suspense>
      );
    case 'inbox':
    case 'source':
      return <InboxScreen />;
    case 'settings':
    case 'settingsPage':
      return <SettingsScreen page={route.params.page ?? null} />;
    default:
      return <TripsScreen />;
  }
}

/** Desktop: trip list and trip detail side by side. */
function TripsPanes({ tripId }: { tripId?: string }) {
  return (
    <div className={s.panes}>
      <div className={s.listPane} data-scroll>
        <TripsScreen pane selectedId={tripId} />
      </div>
      <div className={s.detailPane} data-scroll>
        {tripId ? <TripScreen key={tripId} tripId={tripId} pane /> : <TripScreen.Placeholder />}
      </div>
    </div>
  );
}

function Overlay({ route, open }: { route: Route; open: boolean }) {
  const close = () => back(route.name === 'plan' ? `/trips/${route.params.tripId}` : '/');
  switch (route.name) {
    case 'plan':
      return <PlanSheet itemId={route.params.itemId!} open={open} onClose={close} />;
    case 'newPlan':
      return (
        <PlanEditor
          open={open}
          onClose={close}
          tripId={route.query.get('trip')}
          initialType={(route.query.get('type') as never) ?? null}
        />
      );
    case 'editPlan':
      return <PlanEditor open={open} onClose={close} itemId={route.params.itemId!} />;
    case 'walletNew':
    case 'walletEdit':
      return (
        <WalletEditor
          open={open}
          onClose={() => back('/wallet')}
          kind={route.params.kind as WalletKind}
          recordId={route.params.recordId}
          programId={route.query.get('program')}
        />
      );
    default:
      return null;
  }
}

/** Keeps the last overlay mounted while it plays its exit. */
function useOverlay(route: Route) {
  const [last, setLast] = useState<Route | null>(route.overlay ? route : null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (route.overlay) setLast(route);
    else if (last) timer.current = setTimeout(() => setLast(null), 260);
  }, [route, last]);
  return last;
}

export function Shell() {
  const desktop = useMedia(DESKTOP);
  const { route, background } = useRouter();
  const base = route.overlay ? (background ?? defaultBase(route)) : route;
  const overlay = useOverlay(route);
  // Tabs only on the four main screens; inside a trip, flight or search there's a back button.
  const showTabs = !desktop && TAB_ROOTS.has(base.name);

  return (
    <div className={desktop ? s.desktop : s.phone} data-tabs={showTabs || undefined}>
      {desktop && <Sidebar route={base} />}
      <main className={s.main} data-scroll={desktop || undefined}>
        <ScreenFor route={base} desktop={desktop} />
      </main>
      {showTabs && <TabBar active={base.tab ?? 'trips'} />}
      {isNative && <UpdatePrompt />}
      {!desktop && <PullToRefresh />}
      {overlay && <Overlay route={overlay} open={route.overlay && overlay === route} />}
    </div>
  );
}
